/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import {
  PdfViewer,
  EscalationRejectionModal,
  NextDocumentModal,
} from 'fluent-components';
import RecordForm, {
  RecordFormData,
} from '@/pages/DocumentReviewPage/RecordForm';
import DocumentReviewButton from '@/components/ui/DocumentReviewButton';
import { Toaster } from '@/components/ui/toaster';
import DocumentHeader from '@/components/common/DocumentHeader';
import { useNavigate } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import approvedIcon from '@/assets/approvedIcon.svg';
import { format } from 'date-fns';
import {
  boomWorkflow,
  labResultLoinc,
  newPermissionsFeatureCodes,
  taskQueryBusinessStatus,
} from '@/constants';
import { fhirAPI } from '@/lib/axios';
import { toast } from '@/components/hooks/use-toast';
import { Task } from '@/types/recordManagement';
import {
  cleanObject,
  // createAuditEventPayload,
  createFHIRBundle,
  createTaskPayload,
  formatTime,
  getDocumentStatus,
  getPractitionerId,
  getTaskId,
  isNewDay,
  parsePatientName,
  removeNullAndEmptyObjects,
  removeTypeName,
} from '@/lib/utils';
import useUserInfo from '@/components/Authenticate/useUserInfo';
import {
  ESCALATION_AND_REJECTED_QUERY,
  FAMILY_MEMBER_LIST,
  SINGLE_TASK_QUERY,
  TASK_QUERY,
} from '@/graphql/queries/record-management';
import { documentReferenceAPIs } from '@/lib/axios/apis/recordManagement/document';
import { AuditEventAPI } from '@/lib/axios/apis/recordManagement/auditEvent';
import EmptyStateScreen from '../EmptyStateScreen';
import NextDocumentLoading from './NextDocumentLoading';
import {
  DocumentReferenceType,
  Coding,
  DocumentReviewOptions,
  CommunicationRequestType,
  BundleType,
} from '@/types';
import {
  // CODE_SYSTEM,
  LOINC_FHIR_URL,
} from '@/constants/api-endpoints.constants';
import { eventBus } from '../../eventBus';
import { mixPanelServiceInstance } from '@/analytics/mixpanel';
import { mixPanelEvents } from '@/analytics/mixpanel/events';
import { createCommunicationRequestPayload } from '@/components/common/ValidateForm/fhirTaskBundle';

const DocumentReview: React.FC<{
  showHeader?: boolean;
  customButtons?: React.ReactNode;
  showIcons?: boolean;
  setRecordFormData?: (data: RecordFormData) => void;
  isSupervisor?: boolean;
  escalatedData?: Task;
  otherData?: any;
}> = ({
  customButtons,
  showHeader = true,
  setRecordFormData,
  isSupervisor,
  escalatedData,
  otherData,
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<RecordFormData>({
    recordDate: null,
    doctorName: '',
    title: '',
    nameOnRecord: '',
    typeOfRecord: { value: '', label: '' },
    organisationName: '',
  });
  const [isClassifiedFail, setIsClassifiedFail] = useState<boolean>(false);
  const [document, setDocument] = useState();
  const [patientName, setPatientName] = useState<string>();
  const [documentRef, setDocumentRef] = useState<DocumentReferenceType | null>(
    null
  );
  const [disableOnsubmit, setDisableOnSubmit] = useState<boolean>(false);
  const { data: escalateRejectedList } = useQuery<any>(
    ESCALATION_AND_REJECTED_QUERY
  );

  const [getFamilyMember, { data: familyMemberList }] = useLazyQuery<any>(
    FAMILY_MEMBER_LIST,
    {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    }
  );

  const {
    documentEscalationReasons,
    documentRejectionReasons,
    documentLegalEscalationReasons,
  } = escalateRejectedList || {};
  const rejectionReason =
    documentRejectionReasons?.[0]?.compose?.include?.[0]?.concept?.map(
      (reason: any) => ({ label: reason.display, value: reason.code })
    ) || [];
  const escalateReason =
    documentEscalationReasons?.[0]?.compose?.include?.[0]?.concept?.map(
      (reason: any) => ({ label: reason.display, value: reason.code })
    ) || [];
  const legalEscalationReason =
    documentLegalEscalationReasons?.[0]?.compose?.include?.[0]?.concept?.map(
      (reason: any) => ({ label: reason.display, value: reason.code })
    ) || [];
  const { userInfo, userPermissions } = useUserInfo();
  const practitionerId = getPractitionerId(userInfo);
  // Modal states for rejection and escalation
  const [isRejectionModalOpen, setIsRejectionModalOpen] =
    useState<boolean>(false);
  const [isEscalationModalOpen, setIsEscalationModalOpen] =
    useState<boolean>(false);
  const { data, loading } = useQuery(
    isSupervisor ? SINGLE_TASK_QUERY : TASK_QUERY,
    {
      variables: isSupervisor
        ? { id: escalatedData?.id } // For SINGLE_TASK_QUERY, pass only id
        : {
            business_status:
              taskQueryBusinessStatus?.documentReviewBusinessStatus?.toString(),
            owner: `Practitioner/${practitionerId}`,
            fallback_business_status:
              taskQueryBusinessStatus?.documentReviewFallbackBusinessStatus?.toString(),
            count: 1,
            offset: 0,
          },
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    }
  );

  const [type, setType] = useState<string>('');
  const [showNextDocumentModal, setShowNextDocumentModal] =
    useState<boolean>(false);

  const taskData = data?.primary?.[0] || data?.fallback?.[0];
  // TODO: will use this once consent flow is ready to impliment
  // const [fetchConsent, { data: consentData }] =  useLazyQuery(CONSENT_QUERY);

  // useEffect(() => {
  //   if (taskData?.for?.reference) {
  //     fetchConsent({ variables: { patient: taskData.for.reference } });
  //   }
  // }, [taskData?.for?.reference, fetchConsent]);

  // Task Queuing, assigning practitioner and checking for existing tasks
  useEffect(() => {
    const processTaskData = async (taskData: Task[], status: string) => {
      if (!taskData?.length) return;
      const taskId = getTaskId(taskData);
      const payload = createTaskPayload(
        '',
        status,
        taskData[0],
        `Practitioner/${practitionerId}`,
        isSupervisor ? cleanObject(taskData[0]?.output) : undefined
      );

      // TODO: Create data in more fine manner make constants for Coding Objects
      // const event = createAuditEventPayload(
      //   {
      //     who: { reference: `Practitioner/${practitionerId}` },
      //     requestor: false,
      //     name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      //     network: {
      //       address: '',
      //       type: '2',
      //     },
      //   },
      //   {
      //     what: {
      //       reference: taskData?.[0]?.focus?.reference,
      //     },
      //   },
      //   'R',
      //   {
      //     system: `${CODE_SYSTEM}/iso-21089-lifecycle`,
      //     code: 'access',
      //     display: 'Access/View Record Lifecycle Event',
      //   },
      //   isSupervisor
      // );

      if (taskId && payload) {
        await documentReferenceAPIs.updateTask(
          taskId,
          removeNullAndEmptyObjects(removeTypeName(payload))
        );
        await AuditEventAPI.postAuditEvent(event);
      }
    };

    const patientName = taskData?.for?.resource?.name?.[0];
    const fullName = patientName
      ? `${patientName.given.join(' ')} ${patientName.family}`
      : 'Unknown';
    setPatientName(fullName);
    if (data && data?.fallback && data?.fallback?.length) {
      const fallback = data?.fallback;
      const document = fallback?.[0]?.focus?.resource?.content[0]?.attachment;
      setDocument(document);
      setDocumentRef(fallback?.[0]?.focus?.resource);

      if (fallback && fallback?.[0]?.businessStatus) {
        const businessStatus = fallback?.[0]?.businessStatus?.coding?.[0]?.code;

        if (businessStatus === boomWorkflow?.boomClassification?.failed) {
          setIsClassifiedFail(true);
        }
      }
    }

    if (data && data?.primary && data?.primary?.length) {
      const primary = data?.primary;
      const document = primary?.[0]?.focus?.resource?.content[0]?.attachment;
      setDocument(document);
      setDocumentRef(primary?.[0]?.focus?.resource);
      if (primary && primary?.[0]?.businessStatus) {
        const businessStatus = primary?.[0]?.businessStatus?.coding?.[0]?.code;
        if (
          businessStatus === boomWorkflow?.boomClassification?.failed ||
          businessStatus === boomWorkflow?.boomClassification?.escalated ||
          businessStatus === boomWorkflow?.boomClassification?.inProgress
        ) {
          setIsClassifiedFail(true);
        }
      }
    }

    // TO-DO: Need to add similar check for classification & review flow in UI render side
    const getStatusKey = (taskData: Task[], baseStatus: string) => {
      const isBaseStatus =
        taskData[0]?.businessStatus?.coding?.[0]?.code === baseStatus;

      if (isSupervisor) {
        return boomWorkflow.boomSupervisor;
      }
      return isBaseStatus && isClassifiedFail
        ? boomWorkflow.boomClassification
        : boomWorkflow.boomReview;
    };

    if (data?.primary?.length) {
      const workflow: any = getStatusKey(
        data.primary,
        taskQueryBusinessStatus.documentReviewBusinessStatus.find((status) =>
          data?.primary?.some(
            (task: Task) => task.businessStatus?.coding?.[0]?.code === status
          )
        ) ?? taskQueryBusinessStatus.documentReviewBusinessStatus[0]
      );
      let taskStatus;
      if (isSupervisor) {
        taskStatus = isClassifiedFail
          ? workflow?.classificationInProgress
          : workflow?.reviewInProgress;
      } else {
        taskStatus = workflow?.inProgress;
      }
      processTaskData(data.primary, taskStatus);
    } else if (data?.fallback?.length) {
      const processFallbackTasks = async () => {
        const workflow: any = getStatusKey(
          data.fallback,
          taskQueryBusinessStatus.documentReviewFallbackBusinessStatus.find(
            (status) =>
              data?.fallback?.some(
                (task: Task) =>
                  task.businessStatus?.coding?.[0]?.code === status
              )
          ) ?? taskQueryBusinessStatus.documentReviewFallbackBusinessStatus[0]
        );
        await processTaskData(
          data.fallback,
          isSupervisor ? workflow?.reviewStarted : workflow.started
        );
        await processTaskData(
          data.fallback,
          isSupervisor ? workflow?.reviewInProgress : workflow.inProgress
        );
      };

      processFallbackTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, practitionerId, userInfo]);

  useEffect(() => {
    if (documentRef) {
      //NOTE: Moving it into try catch block to handle errors ||
      //  This is for double check else it is been handle on graphql query using errorPolicy
      try {
        getFamilyMember({
          variables: {
            patientId: documentRef?.subject?.reference?.split('/')[1],
          },
        });
      } catch (error) {
        console.error('Error fetching family member list:', error);
      }
    }
  }, [documentRef, document]);

  useEffect(() => {
    const reviewStarted = JSON.parse(
      localStorage.getItem('reviewStarted') || 'false'
    );
    if (reviewStarted) return;

    const documentId = taskData?.focus?.reference?.split('/')?.[1];
    const reviewerId = taskData?.owner?.reference?.split('/')?.[1];
    const supervisorId = taskData?.owner?.reference?.split('/')?.[1];
    const initial_document_type =
      taskData?.focus?.resource?.type?.coding?.[0]?.display;
    let event = null;
    const payload: any = { document_id: documentId };

    if (!isSupervisor && !isClassifiedFail) {
      event = mixPanelEvents.RECORD_MANAGEMENT.DOCUMENT_REVIEW_STARTED;
      payload.reviewer_id = reviewerId;
      payload.initial_document_type = initial_document_type;
    } else if (!isSupervisor && isClassifiedFail) {
      event = mixPanelEvents.RECORD_MANAGEMENT.DOCUMENT_TYPE_REVIEW_STARTED;
      payload.type_reviewer_id = reviewerId;
    } else if (isSupervisor && isClassifiedFail) {
      event =
        mixPanelEvents.RECORD_MANAGEMENT.ESCALATED_DOCUMENT_TYPE_REVIEW_STARTED;
      payload.supervisor_id = supervisorId;
    } else if (isSupervisor && !isClassifiedFail) {
      event =
        mixPanelEvents.RECORD_MANAGEMENT.ESCALATED_DOCUMENT_REVIEW_STARTED;
      payload.supervisor_id = reviewerId;
    }

    if (taskData && event) {
      mixPanelServiceInstance.track(event, payload);
      localStorage.setItem('reviewStarted', 'true');
    }
  }, [taskData]);

  const openRejectionModal = () => {
    const hasPermission =
      userPermissions?.[
        newPermissionsFeatureCodes.reviewer_documents_reject
      ] === 'CHECK_RESULT_ALLOWED';
    if (hasPermission) {
      setIsRejectionModalOpen(true);
    } else {
      toast({
        title: 'Warning',
        description: `You don't have access!`,
        variant: 'destructive',
      });
    }
  };

  const openEscalationModal = () => {
    const hasPermission =
      userPermissions?.[
        newPermissionsFeatureCodes.reviewer_documents_escalate
      ] === 'CHECK_RESULT_ALLOWED';
    if (hasPermission) {
      setIsEscalationModalOpen(true);
    } else {
      toast({
        title: 'Warning',
        description: `You don't have access!`,
        variant: 'destructive',
      });
    }
  };

  function addTypeToDocumentReference(
    documentRef: any,
    codingValue: Coding,
    context?: any
  ): any {
    return {
      ...documentRef,
      type: codingValue, // Assign the new type
      context: context ? context : documentRef?.context, // Assign the new context
    };
  }

  const validateForm = (): boolean => {
    const showToast = (description: string) => {
      toast({
        title: 'Validation Error',
        description,
        variant: 'destructive',
      });
    };

    if (!formData.typeOfRecord?.value) {
      showToast('Type of Record is required.');
      return false;
    }

    const additionalValidations = [
      {
        condition: !formData.nameOnRecord,
        message: 'Name on Record is required.',
      },
      {
        condition:
          formData.recordDate &&
          new Date(formData.recordDate).getTime() > new Date().getTime(),
        message: 'Date of Record cannot be a future date.',
      },
      {
        condition: !formData.recordDate,
        message: 'Date of Record is required.',
      },
      {
        condition: formData?.doctorName && formData.doctorName.length > 100,
        message: 'Doctor name cannot exceed 100 characters.',
      },
      {
        condition:
          formData?.organisationName && formData?.organisationName.length > 100,
        message: 'organisation name cannot exceed 100 characters.',
      },
    ];

    if (isClassifiedFail) return true;

    for (const { condition, message } of additionalValidations) {
      if (condition) {
        showToast(message);
        return false;
      }
    }

    return true;
  };

  const submitDocument = (type: DocumentReviewOptions, output?: any) => {
    const timer = localStorage.getItem('timer') || 0;
    eventBus.emit('resetTimer');
    if (type !== 'Escalate' && type !== 'Reject' && !validateForm()) {
      return;
    }
    setType(type);
    const storedData = JSON.parse(
      localStorage.getItem('documentCount') || '{}'
    );
    const today = new Date().toISOString().split('T')[0];
    let newCount = 1;
    if (!isNewDay()) {
      newCount = storedData.count + 1;
    }
    // TODO: Move mixpanel related code to a separate function/file
    if (isClassifiedFail && !isSupervisor) {
      mixPanelServiceInstance.track(
        mixPanelEvents.RECORD_MANAGEMENT.DOCUMENT_TYPE_REVIEW_COMPLETED,
        {
          document_id: documentRef?.id || '',
          type_reviewer_id: practitionerId || '',
          dtr_status: type || '',
          dtr_final_document_type:
            documentRef?.type?.coding?.[0]?.display || '',
          dtr_time: formatTime(Number(timer) || 0),
        }
      );
    } else if (!isSupervisor && !isClassifiedFail) {
      mixPanelServiceInstance.track(
        mixPanelEvents.RECORD_MANAGEMENT.DOCUMENT_REVIEW_COMPLETED,
        {
          document_id: documentRef?.id || '',
          reviewer_id: practitionerId,
          dr_document_type_status: formData?.typeOfRecord?.label ? true : false,
          review_status: type || '',
          dr_name_on_document: formData.nameOnRecord,
          dr_date_on_document: formData.recordDate
            ? format(formData.recordDate, 'yyyy-MM-dd')
            : null,
          dr_doctor_name: formData.doctorName,
          dr_organisation_name: formData.organisationName,
          dr_time: formatTime(Number(timer) || 0),
          dr_final_document_type: formData.typeOfRecord?.label,
        }
      );
    } else if (isSupervisor && !isClassifiedFail) {
      mixPanelServiceInstance.track(
        mixPanelEvents.RECORD_MANAGEMENT.ESCALATED_DOCUMENT_REVIEW_COMPLETED,
        {
          document_id: documentRef?.id || '',
          edr_status: type || '',
          edr_name_on_document: formData.nameOnRecord,
          edr_type_of_document: formData?.typeOfRecord?.label,
          edr_date_on_document: formData.recordDate
            ? format(formData.recordDate, 'yyyy-MM-dd')
            : null,
          edr_doctor_name: formData.doctorName,
          edr_organisation_name: formData.organisationName,
          edr_escalation_reason: otherData?.issue,
          escalator_id: otherData?.id,
          supervisor_id: practitionerId,
          // edr_document_type_status: formData?.typeOfRecord?.label ? true: false,
          // edr_final_document_type: formData.typeOfRecord?.label,
        }
      );
    } else if (isSupervisor && isClassifiedFail) {
      mixPanelServiceInstance.track(
        mixPanelEvents.RECORD_MANAGEMENT
          .ESCALATED_DOCUMENT_TYPE_REVIEW_COMPLETED,
        {
          document_id: documentRef?.id || '',
          edtr_status: type || '',
          edtr_document_type: formData?.typeOfRecord?.label,
          edtr_escalation_reason: output?.reason,
          supervisor_id: practitionerId,
        }
      );
    }

    localStorage.setItem(
      'documentCount',
      JSON.stringify({ date: today, count: newCount })
    );
    setDisableOnSubmit(true);
    const permissionMap = {
      Approve: newPermissionsFeatureCodes.reviewer_documents_approve,
      Reject: newPermissionsFeatureCodes.reviewer_documents_reject,
      Escalate: newPermissionsFeatureCodes.reviewer_documents_escalate,
    };

    const hasPermission =
      userPermissions?.[permissionMap[type]] === 'CHECK_RESULT_ALLOWED';

    if (!hasPermission) {
      return toast({
        title: 'Warning',
        description: `You don't have access!`,
        variant: 'destructive',
      });
    }

    const taskData = data?.primary?.[0] || data?.fallback?.[0];

    if (!taskData) {
      return console.warn('No valid task data found. Skipping submission.');
    }

    let taskStatus = '';
    let taskCode = '';
    // let auditEventCode = '';
    // let auditEventDisplay = '';
    let documentReference;
    let taskOutput;
    const supervisorStatus = boomWorkflow.boomSupervisor;

    const classificationStatus = isSupervisor
      ? supervisorStatus.classificationRejected
      : boomWorkflow.boomClassification.rejected;

    const reviewStatus = isSupervisor
      ? supervisorStatus.reviewRejected
      : boomWorkflow.boomReview.rejected;

    const escalateClassificationStatus = isSupervisor
      ? supervisorStatus.classificationEscalated
      : boomWorkflow.boomClassification.escalated;

    const escalateReviewStatus = isSupervisor
      ? supervisorStatus.reviewEscalated
      : 'document-engine:boom-reviewer:review:escalated';

      const communicationData: CommunicationRequestType[] = [];
 
    switch (type) {
      case 'Approve':
        if (isClassifiedFail) {
          taskStatus = isSupervisor
            ? supervisorStatus.classificationCompleted
            : boomWorkflow.boomClassification.completed;
          // auditEventCode = 'amend';
          // auditEventDisplay = 'Amend (Update) Record Lifecycle Event';
          documentReference = addTypeToDocumentReference(documentRef, {
            system: 'http://loinc.org',
            code: formData?.typeOfRecord?.value,
            display: formData?.typeOfRecord?.label,
          });
        } else {

          // const getTaskStatus = () => {
          //   if (
          //     formData?.typeOfRecord?.value === labResultLoinc &&
          //     formData?.nameOnRecord === patientName
          //   ) {
          //     return boomWorkflow.boomReview.completed;
          //   } else {
          //     return boomWorkflow.workflowCompleted;
          //   }
          // };
          if(formData?.typeOfRecord?.value !== labResultLoinc) {
            const communicationPayloadApproved = createCommunicationRequestPayload(
            {
              type: "EMAIL",
              subjectReference: taskData?.for?.reference,
              template: 'record-approved',
            }
          )

          const communicationPayloadApprovedPush =
            createCommunicationRequestPayload({
              type: 'APP_PUSH',
              subjectReference: taskData?.for?.reference,
              template: 'record-approved',
            });

          communicationData.push(communicationPayloadApprovedPush);
          communicationData.push(communicationPayloadApproved);
          }
          
          if (isSupervisor) {
            taskStatus =  supervisorStatus.reviewCompleted
              
          } else {
            taskStatus = boomWorkflow.boomReview.completed;
          }
          const familyMember = familyMemberList?.FamilyMemberHistoryList?.find(
            (member: any) => member.name === formData?.nameOnRecord
          );

          taskCode =
            formData?.typeOfRecord?.value === labResultLoinc &&
            formData?.nameOnRecord === patientName
              ? 'in-progress'
              : 'completed';
          // auditEventCode = 'complete';
          // auditEventDisplay = 'Complete Record Lifecycle Event';
          const contextpayload = {
            related: [
              {
                reference: `Task/${taskData.id}`,
              },
              ...(familyMember && formData?.nameOnRecord !== patientName
                ? [
                    {
                      reference: `FamilyMemberHistory/${familyMember.id}`,
                    },
                  ]
                : []),
            ],
            period: {
              start: formData?.recordDate?.toISOString(),
              end: formData?.recordDate?.toISOString(),
            },
          };
          documentReference = addTypeToDocumentReference(
            documentRef,
            {
              system: LOINC_FHIR_URL,
              code: formData?.typeOfRecord?.value,
              display: formData?.typeOfRecord?.label,
            },
            contextpayload
          );
        }

        break;
      case 'Reject': {
        documentReference = addTypeToDocumentReference(documentRef, {
          system: LOINC_FHIR_URL,
          code: formData?.typeOfRecord?.value,
          display: formData?.typeOfRecord?.label,
        });
        taskStatus = isClassifiedFail ? classificationStatus : reviewStatus;
        taskCode = 'rejected';
        // auditEventCode = 'deprecate';
        // auditEventDisplay = 'Deprecate Record Lifecycle Event';
        const communicationPayloadRejectedEmail = createCommunicationRequestPayload({
          type: 'EMAIL',
          subjectReference: taskData?.for?.reference,
          template: 'records-rejected',
        });
        const communicationPayloadRejectedPush = createCommunicationRequestPayload({
          type: 'APP_PUSH',
          subjectReference: taskData?.for?.reference,
          template: 'records-rejected',
        });

        communicationData.push(communicationPayloadRejectedEmail);
        communicationData.push(communicationPayloadRejectedPush);
        
        taskOutput = [
          {
            type: {
              coding: [
                {
                  code: output.code,
                  display: output.reason,
                },
              ],
            },
            valueString: output.description,
          },
        ];
        
        mixPanelServiceInstance.track(
          mixPanelEvents.RECORD_MANAGEMENT.DOCUMENT_REJECTED,
          {
            document_id: documentRef?.id || '',
            rejector_id: practitionerId,
            rejection_reason: output.reason,
            rejection_notes: output.description,
            rejector_role: isSupervisor ? 'supervisor' : 'reviewer',
            rejection_stage: isClassifiedFail
              ? 'type of document review'
              : 'document review',
          }
        );

        break;
      }
      case 'Escalate':
        taskStatus = isClassifiedFail
          ? escalateClassificationStatus
          : escalateReviewStatus;
        // auditEventCode = 'hold';
        // auditEventDisplay = 'Add Legal Hold Record Lifecycle Event';
        taskOutput = [
          {
            type: {
              coding: [
                {
                  code: output.code,
                  display: output.reason,
                },
              ],
            },
            valueString: output.description,
          },
        ];
        mixPanelServiceInstance.track(
          mixPanelEvents.RECORD_MANAGEMENT.DOCUMENT_ESCALATED,
          {
            document_id: documentRef?.id || '',
            escalator_id: practitionerId,
            escalation_reason: output.reason,
            escalation_notes: output.description,
            escalator_role: isSupervisor ? 'supervisor' : 'reviewer',
            escalation_stage: isClassifiedFail
              ? 'type of document review'
              : 'document review',
          }
        );
        break;
      default:
        return console.error('Unsupported document type:', type);
    }

    if (!taskStatus) return;
    const secondTaskPayload = createTaskPayload(
      taskCode,
      boomWorkflow?.workflowCompleted,
      taskData,
      taskData?.for?.reference,
      taskOutput,
    );

    const taskPayload = createTaskPayload(
      taskCode,
      taskStatus,
      taskData,
      `Practitioner/${practitionerId}`,
      taskOutput,
      `${userInfo?.firstName} ${userInfo?.lastName}`
    );

    if (!taskPayload) {
      console.error('Task payload is null. Skipping submission.');
      return;
    }

    const bundleData = createFHIRBundle({
      actionType: type,
      additionalTaskData: null,
      taskData: taskPayload,
      // add this new parameter
      documentReferenceData: documentReference,
      // auditEventData: event,
      communicationRequestData: communicationData,
      practitionerName: formData.doctorName,
      organizationName: formData.organisationName,
      commonData: {
        subject: `Patient/${taskData.for}`,
        requester: `Practitioner/${practitionerId}`,
        patient: `Patient/${taskData.for}`,
        period: undefined,
      },
    });
    fhirAPI
      .post('', bundleData)
      .then(() => {
        toast({
          title: 'Success',
          description: 'Document updated successfully',
          variant: 'default',
        });
        setDisableOnSubmit(false);
        localStorage.removeItem('reviewStarted');
        if (!isSupervisor) {
          setShowNextDocumentModal(true);
        } else {
          navigate('/supervisor');
          localStorage.removeItem('isReviewer');
        }
      })
      .finally(async () => {
        
        const isApprove = type === 'Approve';
        const isReject = type === 'Reject';
        const isLabResult = formData?.typeOfRecord?.value === labResultLoinc;
        const isPatient = formData?.nameOnRecord === patientName;

        if ((isApprove && (!isLabResult || !isPatient)) || isReject) {
          const bundle: BundleType = {
            resourceType: 'Bundle',
            type: 'transaction',
            entry: [],
          };

          if (secondTaskPayload) {
            bundle.entry.push({
              resource: removeNullAndEmptyObjects(secondTaskPayload),
              request: { method: 'PUT', url: `Task/${taskData?.id}` },
            });
            await fhirAPI.post('', bundle);
          }
        }
      })
      .catch(() => {
        setDisableOnSubmit(false);
        toast({
          title: 'Error',
          description: 'Document update failed',
          variant: 'destructive',
        });
      });
  };

  const handlePause = async () => {
    let url = `/review-paused?reviewer_id=${practitionerId}&document_id=${
      taskData?.focus?.reference?.split('/')?.[1]
    }`;
    if (isClassifiedFail) {
      url += `&isClassifiedFail=${isClassifiedFail}`;
    }
    navigate(url);
  };
  useEffect(() => {
    if (!documentRef || !documentRef.content?.length) return;

    const extractedData: RecordFormData = {
      recordDate: null,
      doctorName: '',
      title: '',
      nameOnRecord: '',
      typeOfRecord: { value: '', label: '' },
      organisationName: '',
    };

    const typeCoding = documentRef?.type?.coding?.[0];
    extractedData.typeOfRecord = typeCoding
      ? { value: typeCoding.code, label: typeCoding.display }
      : { value: '', label: '' };
    try {
      const generalInfoContent = documentRef.content.find(
        (content) => content.attachment?.title === 'General Info Extraction'
      );

      const bundleData = generalInfoContent?.attachment?.data;
      if (!bundleData) return;

      const decodedData = atob(bundleData).trim();
      const bundle = JSON.parse(decodedData);
      if (
        !bundle ||
        bundle.bundle.resourceType !== 'Bundle' ||
        !Array.isArray(bundle.bundle.entry)
      )
        return;
      const getMostRecentDate = (dates: string[]) => {
        if (!dates?.length) return null;
        const datesFormat = dates.map((date) => new Date(date));
        const mostRecentDate = new Date(
          Math.max(...datesFormat.map((date) => date.getTime()))
        );
        return mostRecentDate;
      };
      const finalDate =
        typeCoding?.code === labResultLoinc
          ? new Date(bundle?.dateTime?.effectiveDateTime)
          : getMostRecentDate(bundle?.dateTime?.dateTime);

      if (finalDate && isNaN(new Date(finalDate).getTime())) {
        console.error('Invalid date format:', finalDate);
        extractedData.recordDate = null;
      } else {
        extractedData.recordDate = finalDate || null;
      }
      const patientName = taskData?.for?.resource?.name?.[0];
      const fullName = patientName
        ? `${patientName.given.join(' ')} ${patientName.family}`
        : 'Unknown';
      for (const { resource } of bundle?.bundle?.entry ?? []) {
        if (!resource) continue;
        switch (resource.resourceType) {
          case 'Patient': {
            const name =
              resource.name?.length > 0
                ? resource.name
                    ?.map((n: any) =>
                      n.text
                        ? n.text
                        : `${n.given?.join(' ') || ''} ${n.family || ''}`.trim()
                    )
                    .join(', ')
                : resource.text || '';
            const familyMembers =
              familyMemberList?.FamilyMemberHistoryList || [];

            const isPatientOrFamily =
              name.toLowerCase() === fullName.toLowerCase() ||
              familyMembers.some(
                (member: any) =>
                  member.name?.toLowerCase() === name.toLowerCase()
              );
            if (isPatientOrFamily) {
              setPatientName(fullName);
              extractedData.nameOnRecord = name;
            } else {
              setPatientName(fullName);
            }
            break;
          }

          case 'Organization':
            extractedData.organisationName = resource.name || '';
            break;

          case 'Practitioner': {
            const name =
              resource.name?.length > 0
                ? resource.name
                    ?.map((n: any) =>
                      n.text
                        ? n.text
                        : `${n.given?.join(' ') || ''} ${n.family || ''}`.trim()
                    )
                    .join(', ')
                : resource.text || '';
            extractedData.doctorName = name;
            break;
          }

          default:
            break;
        }
      }
    } catch (error) {
      console.error('Error parsing document bundle:', error);
    }
    setFormData(extractedData);
  }, [documentRef, familyMemberList?.FamilyMemberHistoryList]);

  useEffect(() => {
    setRecordFormData?.(formData);
  }, [formData, setRecordFormData]);

  if (loading) {
    return <NextDocumentLoading />;
  }

  if (data?.primary?.length === 0 && data?.fallback?.length === 0) {
    return <EmptyStateScreen message="" />;
  }

  return (
    <div>
      {showHeader && (
        <DocumentHeader
          onPauseHandler={() => handlePause()}
          source={isSupervisor ? 'supervisor' : 'reviewer'}
          taskDetails={taskData}
          isClassifiedFail
        />
      )}

      <div className="px-14 py-6 flex flex-row bg-white rounded-lg shadow-lg h-screen ">
        <div className="w-5/12 flex-shrink-0">
          <PdfViewer file={document} />
        </div>
        <div className="w-7/12 flex-shrink-0 px-4 gap-y-8">
          <RecordForm
            patientId={taskData?.for?.reference?.split('/')[1]}
            nameOnRecord={parsePatientName(patientName)}
            description={otherData?.description}
            formData={formData}
            setFormData={setFormData}
            classifierStatus={isClassifiedFail}
            isSupervisior={isSupervisor}
            familyMemberList={familyMemberList?.FamilyMemberHistoryList}
          />
          <div className="flex w-full justify-between py-6 gap-4">
            {customButtons || (
              <>
                <DocumentReviewButton
                  id="1"
                  initialLabel="Approve"
                  loadingLabel="Loading..."
                  successLabel="Approved"
                  colorClass="border-[1px] rounded-md text-[#20785C] border-[#20785C] h-10 w-1/3"
                  successColorClass="border-[1px] rounded-md bg-[#E5F9F4] text-[#20785C] border-[#20785C] h-10 w-1/3"
                  showIcons={false}
                  icon={approvedIcon}
                  onClick={() => submitDocument('Approve')}
                  disabled={disableOnsubmit}
                />
                <DocumentReviewButton
                  id="2"
                  initialLabel="Reject"
                  loadingLabel="Loading..."
                  successLabel="Rejected"
                  colorClass="border-[1px] rounded-md text-[#CF0A0A] border-[#CF0A0A] h-10 w-1/3"
                  successColorClass="border-[1px] rounded-md text-[#CF0A0A] border-[#CF0A0A] h-10 w-1/3"
                  onClick={openRejectionModal} // Open rejection modal
                  showIcons={false}
                  disabled={disableOnsubmit}
                />
                <DocumentReviewButton
                  id="3"
                  initialLabel="Escalate"
                  loadingLabel="Loading..."
                  successLabel="Escalated"
                  colorClass="border-[1px] rounded-md text-[#495AE4] border-[#495AE4] h-10 w-1/3"
                  successColorClass="border-[1px] rounded-md bg-[#F0F1FF] text-[#3F51E3] border-[#495AE4] h-10 w-1/3"
                  onClick={openEscalationModal} // Open escalation modal
                  showIcons={false}
                  disabled={disableOnsubmit}
                />
              </>
            )}
          </div>

          {/* Rejection Modal */}
          <EscalationRejectionModal
            modalConfig={{
              title: 'Reject Document',
              onClose: () => setIsRejectionModalOpen(false),
              isOpen: isRejectionModalOpen,
              handleSubmit: (output: any) => {
                submitDocument('Reject', output);
                setIsRejectionModalOpen(false);
              },
            }}
            // TODO: we need to add options from valueset
            reasonDropdownConfig={{
              options: rejectionReason,
              label: 'Select reason for rejection*',
            }}
          />

          {/* Escalation Modal */}

          <EscalationRejectionModal
            modalConfig={{
              title: 'Escalate Document',
              onClose: () => setIsEscalationModalOpen(false),
              isOpen: isEscalationModalOpen,
              handleSubmit: (output: any) => {
                submitDocument('Escalate', output);
                setIsEscalationModalOpen(false);
              },
            }}
            reasonDropdownConfig={{
              options: isSupervisor
                ? legalEscalationReason
                : [...escalateReason, ...legalEscalationReason],
              label: 'Select reason for escalation*',
            }}
          />

          <NextDocumentModal
            modalConfig={{
              title: getDocumentStatus(type),
              onClose: () => {
                setShowNextDocumentModal(false);
                navigate('/');
              },
              isOpen: showNextDocumentModal,
              handleSubmit: () => window.location.reload(),
              handlePause: handlePause,
            }}
          />

          <Toaster />
        </div>
      </div>
    </div>
  );
};

export default DocumentReview;
